import { useCallback, useEffect, useRef, useState } from "react";
import { getPublicRental, createPublicDepositHold } from "@fm/api";

export type DepositStage =
  | "idle"
  | "waiting_secret"
  | "confirming"
  | "succeeded"
  | "failed"
  | "skipped"
  // The backend plans the automatic hold for later (e.g. pickup minus the
  // lead time). The booking is confirmed; there is nothing to wait for now.
  | "scheduled"
  // Backend rejected the rental's PaymentMethod (debit/prepaid/Link/etc.).
  // The customer must provide a fresh credit card before the hold can be
  // placed.
  | "needs_card";

interface UseDepositHoldFlowArgs {
  /**
   * Enable the flow. Pass `false` when the rental doesn't require a deposit
   * or when the prerequisites (rentalId / accessToken) aren't ready yet —
   * the hook will stay in `idle` until enabled.
   */
  enabled: boolean;
  rentalId: string | null;
  accessToken: string | null;
  /**
   * PaymentMethod id captured during the rental confirmation. The hook will
   * try to reuse it for the deposit hold before prompting the customer for
   * a new card. Pass `null` when no PaymentMethod was captured (e.g., the
   * customer paid with Link).
   */
  rentalPaymentMethodId: string | null;
}

interface UseDepositHoldFlowResult {
  stage: DepositStage;
  errorMessage: string | null;
  retryCount: number;
  depositPaymentMethodId: string | null;
  /** ISO8601 time of the planned automatic hold, when the backend reports it. */
  scheduledAt: string | null;
  /**
   * Manually re-attempt the hold with the supplied PaymentMethod id. Called
   * by `<DepositCardCapture>` after the customer provides a credit card.
   */
  requestDepositHold: (paymentMethodId: string) => Promise<void>;
  /** Setter exposed so the consumer can persist the captured deposit card. */
  setDepositPaymentMethodId: (id: string | null) => void;
  /** Manual retry trigger used by `<DepositHoldStatus>`. */
  retry: () => void;
}

// Stripe error codes that mean "this card cannot back the hold". For any of
// these the customer needs a different card — retrying the same PM will fail
// the same way.
const CARD_REJECTION_CODES = new Set([
  "card_declined",
  "expired_card",
  "incorrect_cvc",
  "incorrect_number",
  "incorrect_zip",
  "processing_error",
  "card_not_supported",
  "currency_not_supported",
  "authentication_required",
  "insufficient_funds",
]);

const MAX_POLL_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 1500;
// A hold planned within this window is about to be run by the webhook, so we
// keep waiting instead of settling as `scheduled`.
const SCHEDULED_MIN_LEAD_MS = 60_000;

function isScheduledForLater(scheduledAt: string | null | undefined): boolean {
  if (!scheduledAt) return false;
  const time = Date.parse(scheduledAt);
  if (Number.isNaN(time)) return false;
  return time - Date.now() > SCHEDULED_MIN_LEAD_MS;
}

/**
 * Drives the post-checkout deposit-hold sequence:
 *   1. Poll the public rental endpoint to see if the backend webhook already
 *      authorized the hold automatically (rental PM was credit).
 *   2. If still pending, retry the hold against the rental's PM. The backend
 *      rejects non-credit cards with `code: "deposit_card_must_be_credit"`,
 *      which flips the stage to `needs_card`.
 *   3. The host UI renders a `<DepositCardCapture>` form whose `onCaptured`
 *      callback hands a fresh PM id back into `requestDepositHold`.
 *
 * Duplicated from quote-review intentionally — the patterns match but they're
 * decoupled so refactors in one flow don't ripple into the other.
 */
export function useDepositHoldFlow({
  enabled,
  rentalId,
  accessToken,
  rentalPaymentMethodId,
}: UseDepositHoldFlowArgs): UseDepositHoldFlowResult {
  const [stage, setStage] = useState<DepositStage>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [scheduledAt, setScheduledAt] = useState<string | null>(null);
  const [depositPaymentMethodId, setDepositPaymentMethodIdState] = useState<
    string | null
  >(null);

  // We mirror the deposit PM id into a ref so the polling loop can read it
  // without being a dependency (would re-create the loop on each setter call).
  const depositPmRef = useRef<string | null>(null);
  depositPmRef.current = depositPaymentMethodId;

  const rentalPmRef = useRef<string | null>(null);
  rentalPmRef.current = rentalPaymentMethodId;

  const requestDepositHold = useCallback(
    async (paymentMethodId: string): Promise<void> => {
      if (!rentalId || !accessToken) {
        setStage("failed");
        setErrorMessage("Missing rental access token.");
        return;
      }

      setStage("confirming");
      setErrorMessage(null);

      try {
        const result = await createPublicDepositHold({
          rentalId,
          token: accessToken,
          stripe_payment_method_id: paymentMethodId,
        });

        if (
          result.data.status === "held" ||
          result.data.status === "succeeded"
        ) {
          setStage("succeeded");
        } else if (result.data.status === "pending") {
          // Hold created but Stripe is waiting on 3DS or similar; the webhook
          // will flip it to `held` once Stripe confirms.
          setStage("waiting_secret");
        } else if (result.data.status === "scheduled") {
          // The fleet's hold is not due yet: the server placed no hold and
          // will place it at `scheduled_at`. Not a failure.
          setScheduledAt(result.data.scheduled_at ?? null);
          setStage("scheduled");
        } else {
          setStage("failed");
          setErrorMessage(
            `Unexpected deposit status: ${result.data.status ?? "unknown"}.`,
          );
        }
      } catch (err: any) {
        const code = err?.response?.data?.error?.code;
        const message =
          err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          err?.message ||
          "";

        // Backend already has an active hold (the webhook beat us to it, or
        // the customer double-clicked retry). This is not a failure — the
        // hold exists. Drop back into the polling state so the next tick of
        // `getPublicRental` flips `deposit_required` to false and we close
        // the flow as succeeded. Without this branch the customer sees a red
        // "An active deposit hold already exists" error while the hold was
        // actually placed successfully.
        if (/active deposit hold already exists/i.test(message)) {
          setStage("waiting_secret");
          setErrorMessage(null);
          return;
        }

        const cardRejectionMessage =
          /insufficient funds/i.test(message) ||
          /declined/i.test(message) ||
          /expired/i.test(message) ||
          /incorrect/i.test(message);

        const needsFreshCard =
          code === "deposit_card_must_be_credit" ||
          code === "stripe_error" ||
          CARD_REJECTION_CODES.has(code) ||
          cardRejectionMessage ||
          /previously used/i.test(message) ||
          /detached from a customer/i.test(message);

        if (needsFreshCard) {
          setStage("needs_card");
          setErrorMessage(
            code === "deposit_card_must_be_credit"
              ? "Your card was not accepted as a credit card. Please add a different credit card to authorize the security deposit."
              : message
                ? `${message} Please try a different credit card.`
                : "We could not authorize the security deposit on that card. Please try a different credit card.",
          );
        } else {
          setStage("failed");
          setErrorMessage(
            message ||
              "An unexpected error occurred while authorizing the deposit.",
          );
        }
      }
    },
    [rentalId, accessToken],
  );

  // Poll the public rental endpoint while the deposit is pending. When the
  // backend webhook authorizes the hold automatically (rental PM was credit),
  // `deposit_required` flips to `false` and we mark the stage as `succeeded`.
  useEffect(() => {
    if (!enabled) return;
    if (!rentalId || !accessToken) return;

    // Already in a terminal/blocking state — stop here. `needs_card` is
    // blocking on the customer providing a fresh credit card, so don't loop
    // back into requestDepositHold automatically.
    if (
      stage === "confirming" ||
      stage === "succeeded" ||
      stage === "scheduled" ||
      stage === "failed" ||
      stage === "needs_card"
    ) {
      return;
    }

    let cancelled = false;
    let attempts = 0;

    // The polling logic is deterministic — it relies on three backend signals
    // (no heuristic timeouts):
    //
    //   deposit_required           : does this rental still need a hold?
    //   deposit_hold_in_progress   : is there already a Payment with status
    //                                :pending or :held for this rental?
    //   deposit_hold_attempted_at  : has the webhook finished its automatic
    //                                attempt (success, skip, or failure)?
    //
    // Decision table:
    //   deposit_required=false                       → succeeded ✓
    //   deposit_hold_in_progress=true                → wait (webhook or Stripe
    //                                                 is mid-flight; the next
    //                                                 poll will see _required=false)
    //   attempted_at=null && scheduled_at > now+60s  → scheduled ✓ (the hold
    //                                                 is planned for later)
    //   attempted_at=null                            → wait (webhook hasn't
    //                                                 finished trying yet);
    //                                                 when polling is
    //                                                 exhausted → scheduled
    //   attempted_at!=null && depositPm provided     → customer gave a card,
    //                                                 confirm with it
    //   attempted_at!=null && rentalPm available     → backend chose not to
    //                                                 hold automatically (PM
    //                                                 isn't credit); we may
    //                                                 still try the rental PM
    //                                                 to surface the precise
    //                                                 reason via the response
    //                                                 code (it will flip to
    //                                                 needs_card)
    //   attempted_at!=null && no PMs                 → needs_card
    const poll = async (isFinal = false) => {
      try {
        const response = await getPublicRental({
          rentalId,
          token: accessToken,
        });

        if (cancelled) return;

        if (response.data.deposit_required === false) {
          setStage("succeeded");
          return;
        }

        if (response.data.deposit_hold_in_progress === true) {
          return;
        }

        // Webhook hasn't finished its automatic attempt yet — wait. Without
        // this gate the embed would race the webhook and one of them would
        // hit 409 "active deposit hold already exists".
        if (!response.data.deposit_hold_attempted_at) {
          const plannedFor = response.data.deposit_hold_scheduled_at ?? null;
          if (isFinal || isScheduledForLater(plannedFor)) {
            setScheduledAt(plannedFor);
            setStage("scheduled");
          }
          return;
        }

        const depositPm = depositPmRef.current;
        if (depositPm) {
          await requestDepositHold(depositPm);
          return;
        }

        const rentalPm = rentalPmRef.current;
        if (rentalPm) {
          await requestDepositHold(rentalPm);
          return;
        }

        setStage("needs_card");
        setErrorMessage(
          "Please provide a credit card to authorize the security deposit.",
        );
      } catch {
        // Non-fatal: keep polling until maxAttempts is reached.
      }
    };

    setStage("waiting_secret");
    void poll();

    const interval = setInterval(() => {
      attempts += 1;
      if (attempts >= MAX_POLL_ATTEMPTS) {
        clearInterval(interval);
        // Final poll: settle instead of leaving the spinner up forever.
        void poll(true);
        return;
      }
      void poll();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [enabled, rentalId, accessToken, stage, requestDepositHold]);

  const setDepositPaymentMethodId = useCallback((id: string | null) => {
    setDepositPaymentMethodIdState(id);
  }, []);

  const retry = useCallback(() => {
    setRetryCount((n) => n + 1);
    const pm = depositPmRef.current ?? rentalPmRef.current;
    if (pm) {
      void requestDepositHold(pm);
    } else {
      setStage("waiting_secret");
    }
  }, [requestDepositHold]);

  return {
    stage,
    errorMessage,
    retryCount,
    depositPaymentMethodId,
    scheduledAt,
    requestDepositHold,
    setDepositPaymentMethodId,
    retry,
  };
}
